import { animated } from '@react-spring/web';
import React, { FC, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from '@xstyled/styled-components';
import { useFade } from '~/lib/transitions';
import { appTheme } from '~/shell/theme';
import { AnyStyledComponent } from 'styled-components';

let inertLockCount = 0;

function setRootInert(inert: boolean) {
  const rootEl = document.getElementById('root');
  if (!rootEl) return;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore — inert is supported in modern browsers
  rootEl.inert = inert;
}

const AnimatedDiv = animated.div as AnyStyledComponent;
const Styled = styled(AnimatedDiv)<{ $allowScroll?: boolean }>`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  perspective: 1000px;
  overflow-x: hidden;
  overflow-y: ${p => (p.$allowScroll ? 'auto' : 'hidden')};
  z-index: ${p => p.theme.zIndices.overlay};
`;

export const Overlay: FC<{
  open: boolean;
  onClick?: () => void;
  opacity?: number;
  dataTestId?: string;
  /** Allow vertical scroll (e.g. onboarding on short viewports). */
  allowScroll?: boolean;
}> = ({
  open,
  onClick,
  children,
  opacity = 0.8,
  dataTestId,
  allowScroll = false
}) => {
  const transition = useFade(
    open,
    'backgroundColor',
    appTheme.colors.overlayClear,
    appTheme.colors.overlayDark
  );

  useEffect(() => {
    if (!open) return;

    inertLockCount += 1;
    setRootInert(true);

    return () => {
      inertLockCount = Math.max(0, inertLockCount - 1);
      setRootInert(inertLockCount > 0);
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return createPortal(
    <nav>
      {transition(
        (styles, item) =>
          item && (
            <Styled
              $allowScroll={allowScroll}
              style={
                opacity
                  ? { ...styles, backgroundColor: `rgba(0, 0, 0, ${opacity})` }
                  : styles
              }
              onClick={onClick}
              data-test-id={dataTestId}
            >
              {children}
            </Styled>
          )
      )}
    </nav>,
    document.body
  );
};
